<script setup>
import { defineProps, ref, defineEmits } from 'vue';
import Checkbox from '@/components/checkbox.vue';

const emit = defineEmits(['update:show', 'isConfirmed']);

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  bidId: {
    type: String,
    required: true
  }
});

const toggleCloseButton = () => {
    emit('update:show', false);
    emit('isConfirmed', 'close', props.bidId);
};

const isConfirmed = ref(false);

</script>

<template>
    <Teleport to="body">
        <div v-if="props.show" 
             class="fixed inset-0 z-50 flex items-center justify-center bg-echoes-background-black/50 backdrop-blur-sm">
            <div 
                class="bg-echoes-background-gray rounded-[21px] shadow-xl p-5 min-w-[280px] animate-slideIn overflow-hidden border border-echoes-border-light">
                <div class="flex flex-col gap-6">
                    <div class="space-y-6">
                        <div class="flex justify-between items-center border-b pb-4 border-echoes-border-light">
                        <h5 class="font-semibold text-echoes-gray text-base md:text-lg">Review Offer</h5>
                        <button type="button"
                                @click="toggleCloseButton"
                                class="text-white bg-transparent hover:bg-white/10 hover:text-red-600 transition-colors duration-300 ease-in-out rounded-lg text-sm p-1.5 ms-auto inline-flex items-center">
                            <span class="sr-only">Close modal</span>
                            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center justify-start gap-2">
                                <Checkbox v-model="isConfirmed" />
                                <h3 class="text-echoes-primary font-medium text-sm md:text-base">Confirm Bid Review</h3>
                            </div>
                            <div>
                                <p class="text-white/80 text-xs md:text-base mt-1">If I choose to accept this bid, I confirm it is binding and the buyer may purchase at this price.</p>
                            </div>
                        </div>
                    </div>
                    <div class="flex gap-3">
                        <button 
                            class="flex-1 bg-echoes-primary text-white py-2 px-4 text-sm md:text-base rounded-[13px] font-semibold transition-colors flex items-center justify-center gap-2"
                            @click="() => { emit('update:show', false); emit('isConfirmed', 'accept', props.bidId); }"
                            :disabled="!isConfirmed"
                            :class="{ 'opacity-50 cursor-not-allowed': !isConfirmed, 'hover:bg-echoes-primary/80 hover:text-white': isConfirmed }"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                            Accept
                        </button>
                        <button 
                            class="flex-1 bg-white/10 text-white py-2 px-4 text-sm md:text-base rounded-[13px] font-semibold transition-colors flex items-center justify-center gap-2"
                            @click="() => { emit('update:show', false); emit('isConfirmed', 'reject', props.bidId); }"
                            :disabled="!isConfirmed"
                            :class="{ 'opacity-50 cursor-not-allowed': !isConfirmed, 'hover:bg-white/5 ': isConfirmed }"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                            Reject
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>